import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { get, post, type Invite } from '../api'
import { Button, Copyable, Field, Notice, Panel } from '../components/ui'
import { useSession } from '../session'
import { cabinetBase } from '../pages/ApiTokens'
import { UsersList } from './users/List'
import { isoDate } from './network/format'

/*
 * Users in the console: the list with plans and usage (BILL-B6a, ./users/List.tsx), the user
 * card at /admin/users/:id (./users/Card.tsx), and invites. Console features, block and unblock
 * moved to the card.
 */
export function AdminUsers() {
  const qc = useQueryClient()
  const { user } = useSession()
  const invites = useQuery({ queryKey: ['admin-invites'], queryFn: () => get<Invite[]>('/api/admin/invites') })
  const [code, setCode] = useState('')
  const [note, setNote] = useState('')
  const [err, setErr] = useState('')
  const invite = async () => {
    setErr('')
    try {
      const r = await post<{ code: string }>('/api/admin/invites', { note })
      setCode(r.code)
      setNote('')
      await qc.invalidateQueries({ queryKey: ['admin-invites'] })
    } catch (e) {
      setErr((e as Error).message)
    }
  }
  // Accounts are created in the cabinet: on the admin name (docs/deploy.md «Имена») the
  // link must point at the user name, where registration is (AUTH-REG-1).
  const link = code ? `${cabinetBase(user?.app_url, user?.area)}/register?invite=${encodeURIComponent(code)}` : ''
  return (
    <div className="space-y-6">
      <UsersList />
      <Notice>{err}</Notice>
      <Panel title="Invites">
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-48 flex-1">
            <Field label="Who is it for" name="note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Name or email, for the log" />
          </div>
          <Button variant="primary" onClick={invite}>
            Create invite
          </Button>
        </div>
        {code && (
          <div className="mt-4 space-y-2" data-testid="invite-result">
            <p className="text-sm">Send this link. The code works once, for 7 days, and is shown only now.</p>
            <Copyable label="Invite link" value={link} />
            <Copyable label="Invite code" value={code} />
          </div>
        )}
        {!!invites.data?.length && (
          <ul className="mt-4 divide-y divide-line text-sm">
            {invites.data.slice(0, 10).map((i) => (
              <li key={i.id} className="flex justify-between gap-3 py-2">
                <span>{i.note || 'No note'}</span>
                <span className="text-muted">
                  {i.used_email ? `used by ${i.used_email}` : Date.parse(i.expires_at) < Date.now() ? 'expired' : `open, expires ${isoDate(i.expires_at)}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  )
}
